import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";

// Owner-only read of the full profile row (including the private birthdate).
// Used exclusively by the owner's own edit form — every other group member
// reads through getGroupProfiles below, which never returns birthdate.
// childNameId null/undefined => the user's personal profile; a names.id =>
// that specific Child List's profile.
export async function getProfile({ userId, childNameId }) {
  const base = supabase.from("name_profiles").select("*");
  const query =
    childNameId == null
      ? base.eq("user_id", userId).is("child_name_id", null)
      : base.eq("child_name_id", childNameId);

  const { data, error } = await query.maybeSingle();

  if (error) {
    logger.error('nameProfilesService', 'get_profile_failed', {
      userId,
      childNameId: childNameId ?? null,
      error: error.message,
    });
    throw error;
  }
  return data ?? null;
}

// Group-wide read via the SECURITY DEFINER RPC: returns age_range (never the raw
// birthdate) plus the non-sensitive fields, only for names that have a profile
// row, and only if the caller is a member of the group. Signature and output are
// unchanged by the subject re-key — name_id is still the reading group's names.id.
export async function getGroupProfiles(groupId) {
  const { data, error } = await supabase.rpc("get_group_name_profiles", { p_group_id: groupId });

  if (error) {
    logger.error('nameProfilesService', 'get_group_profiles_failed', { groupId, error: error.message });
    throw error;
  }
  return data ?? [];
}

// Copies the date of birth captured during signup into the user's personal
// name_profiles row exactly once. Unlike upsertProfile (a full-row replace),
// this is a targeted write: it never disturbs another profile field and never
// overwrites a birthdate that's already set. Called fire-and-forget from
// App.js's post-login profile load, reading the value out of auth metadata.
// Deliberately keeps the raw date out of every logged payload.
export async function setBirthdateAtSignup({ userId, birthdate }) {
  if (!userId || !birthdate) return null;

  const { data: existing, error } = await supabase
    .from("name_profiles")
    .select("id, birthdate")
    .eq("user_id", userId)
    .is("child_name_id", null)
    .maybeSingle();

  if (error) {
    logger.error('nameProfilesService', 'signup_birthdate_read_failed', { userId, error: error.message });
    throw error;
  }

  // Already has a birthdate on file — never clobber it.
  if (existing?.birthdate) return existing;

  if (existing) {
    const { data, error: updateError } = await supabase
      .from("name_profiles")
      .update({ birthdate, updated_at: new Date().toISOString() })
      .eq("id", existing.id)
      .select()
      .single();

    if (updateError) {
      logger.error('nameProfilesService', 'signup_birthdate_update_failed', { userId, error: updateError.message });
      throw updateError;
    }

    logger.info('nameProfilesService', 'signup_birthdate_set', { userId, mode: 'update' });
    return data;
  }

  const { data, error: insertError } = await supabase
    .from("name_profiles")
    .insert({ user_id: userId, child_name_id: null, birthdate, updated_at: new Date().toISOString() })
    .select()
    .single();

  if (insertError) {
    const isDuplicate =
      insertError.code === "23505" ||
      /duplicate key value violates unique constraint/i.test(insertError.message || "");
    if (isDuplicate) {
      // A concurrent writer (e.g. a second tab finishing login) created the row
      // first — nothing to do, and its birthdate write already covered this.
      logger.info('nameProfilesService', 'signup_birthdate_set', { userId, mode: 'insert_race' });
      return null;
    }
    logger.error('nameProfilesService', 'signup_birthdate_insert_failed', { userId, error: insertError.message });
    throw insertError;
  }

  logger.info('nameProfilesService', 'signup_birthdate_set', { userId, mode: 'insert' });
  return data;
}

// Removes the signup date of birth from auth metadata once setBirthdateAtSignup
// has landed it in name_profiles — the raw date has no reason to persist in
// auth.users.raw_user_meta_data after that. Best-effort data-minimization
// cleanup: returns false (rather than throwing) on the documented { error }
// result so the caller's fire-and-forget chain isn't disturbed, and a failure
// just retries on the next login. Keeps the raw date out of every logged
// payload. updateUser merges per key, so user_id / display_name /
// invited_group_id are left untouched.
export async function clearSignupBirthdate() {
  const { error } = await supabase.auth.updateUser({ data: { birthdate: null } });

  if (error) {
    logger.warn('nameProfilesService', 'signup_birthdate_auth_clear_failed', { error: error.message });
    return false;
  }

  logger.info('nameProfilesService', 'signup_birthdate_auth_cleared', {});
  return true;
}

export async function upsertProfile({
  userId,
  childNameId,
  birthdate,
  shirtSize,
  pantsSize,
  pantsSizeCategory,
  shoeSize,
  favoriteBrands,
  interests,
  notes,
}) {
  // The category is only stored alongside a size — never an orphan category.
  // Always sent, since the upsert replaces the whole row.
  const storedPantsCategory = pantsSize ? pantsSizeCategory || null : null;

  const { data, error } = await supabase
    .from("name_profiles")
    .upsert(
      {
        user_id: userId,
        child_name_id: childNameId ?? null,
        birthdate: birthdate || null,
        shirt_size: shirtSize || null,
        pants_size: pantsSize || null,
        pants_size_category: storedPantsCategory,
        shoe_size: shoeSize || null,
        favorite_brands: favoriteBrands?.length ? favoriteBrands : null,
        interests: interests?.length ? interests : null,
        notes: notes?.trim() || null,
        // No DB trigger touches updated_at on this table — set it here on every write.
        updated_at: new Date().toISOString(),
      },
      { onConflict: "user_id,child_name_id" }
    )
    .select()
    .single();

  if (error) {
    logger.error('nameProfilesService', 'upsert_profile_failed', {
      userId,
      childNameId: childNameId ?? null,
      error: error.message,
    });
    throw error;
  }

  // Deliberately no raw birthdate or exact age in the logged payload — this data
  // is privacy-sensitive and must not be duplicated into app_logs.
  logger.info('nameProfilesService', 'profile_updated', {
    userId,
    childNameId: childNameId ?? null,
    shirtSize: shirtSize || null,
    pantsSize: pantsSize || null,
    pantsSizeCategory: storedPantsCategory,
    shoeSize: shoeSize || null,
    favoriteBrands: favoriteBrands?.length ? favoriteBrands : null,
    interests: interests?.length ? interests : null,
    hasNotes: !!notes?.trim(),
    birthdateSet: !!birthdate,
  });

  return data;
}
